// ============================================================================
// STATISTIQUES › VENTES › OBJECTIFS — jour après jour : la cible, le réalisé, et l'an dernier en face (un samedi se
// compare à un samedi). Réf. maquette du back-office (écran `svo`) et docs/CADRAGE-Statistiques-Ventes.md §11.
//
// ⚠️ L'ÉCRAN DESSINE, IL NE DÉFINIT AUCUN NOMBRE : objectif de chaque jour, cumuls, atteinte, écart, reste à faire et
//    projection viennent du serveur (`sales/objectives.ts`). Ce qui se calcule ici est de la GÉOMÉTRIE.
// ⚠️ LA PROJECTION EST NOMMÉE COMME TELLE : « au rythme actuel », jamais un réalisé.
// ⚠️ SA PÉRIODE EST LA SIENNE : un objectif court jusqu'au bout de son mois, et ses jours à venir portent une cible — la
//    période des autres onglets s'arrête à aujourd'hui.
// ============================================================================
import type { CSSProperties, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Icon, useChartTip } from '@orgazone/ui-web';
import type { IconName } from '@orgazone/ui-tokens';
import { currentCurrency, toInputValue } from '@orgazone/money-format';
import type { ObjectiveTrackingRow, ObjectiveTrackingView, SalesSummaryTotals } from '../api';
import { key, keyText, nombre, t, type Translatable } from '../i18n';
import { moisLongLisible } from '../periode';
import { exporterCsv } from './export-csv';
import { isoOf, type StatsWindow } from './stats-bar';
import { csvQty, csvRate, dTx, eur0, pc } from './stats-format';
import { useStatsColumns } from './stats-grid';
import { CardHead, KPI_PAD_X, KPI_TILE, KpiLabel, TONES, axisLabel, monotonePath, niceTop, sparkPaths, type Tone } from './stats-resume';

/** L'unité d'une colonne : un montant, un taux, un indice de vente, une quantité. */
type Unit = 'money' | 'rate' | 'idv' | 'qty';

/** Une colonne du tableau « Jour après jour ». `n1` : la valeur de l'an dernier ; `locked` : jamais masquée. */
export interface ObjectiveColumn { k: string; label: Translatable; unit: Unit; n1?: true; locked?: true }

/** Les colonnes, dans l'ordre de la maquette : la cible et l'atteinte d'abord, puis l'an dernier en face de chaque indicateur. */
export const OBJECTIVE_COLUMNS: readonly ObjectiveColumn[] = [
  { k: 'obj', label: key('Objectif HT'), unit: 'money', locked: true },
  { k: 'caHT', label: key('Ventes HT'), unit: 'money', locked: true },
  { k: 'att', label: key('Atteinte'), unit: 'rate', locked: true },
  { k: 'cumO', label: key('Cumul objectif'), unit: 'money' },
  { k: 'cumC', label: key('Cumul ventes'), unit: 'money' },
  { k: 'ecC', label: key('Écart cumulé'), unit: 'money' },
  { k: 'caN1', label: key('Ventes HT'), unit: 'money', n1: true },
  { k: 'evo', label: key('Évol. N-1'), unit: 'rate' },
  { k: 'ca', label: key('Ventes TTC'), unit: 'money' },
  { k: 'ivN1', label: key('IV'), unit: 'idv', n1: true },
  { k: 'iv', label: key('IV'), unit: 'idv' },
  { k: 'pmN1', label: key('PM'), unit: 'money', n1: true },
  { k: 'pm', label: key('PM'), unit: 'money' },
  { k: 'ttN1', label: key('TT'), unit: 'rate', n1: true },
  { k: 'tt', label: key('TT'), unit: 'rate' },
  { k: 'ppN1', label: key('Passages'), unit: 'qty', n1: true },
  { k: 'pp', label: key('Passages'), unit: 'qty' },
  { k: 'nvN1', label: key('Ventes|nombre de tickets'), unit: 'qty', n1: true },
  { k: 'nv', label: key('Ventes|nombre de tickets'), unit: 'qty' },
  { k: 'naN1', label: key('Articles'), unit: 'qty', n1: true },
  { k: 'na', label: key('Articles'), unit: 'qty' },
  { k: 'mrg', label: key('Marge {symbole} HT'), unit: 'money' },
  { k: 'mrgP', label: key('Marge %'), unit: 'rate' },
  { k: 'rem', label: key('Remise'), unit: 'rate' },
];
const LOCKED = OBJECTIVE_COLUMNS.filter((c) => c.locked).map((c) => c.k);

/** Le libellé d'une colonne — l'an dernier marqué « N-1 » dans le choix des colonnes, où il n'a pas de bandeau. */
const columnLabel = (c: ObjectiveColumn): string => t(c.label, { symbole: currentCurrency().symbol });
const pickerLabel = (c: ObjectiveColumn): string => (c.n1 ? t('{colonne} N-1', { colonne: columnLabel(c) }) : columnLabel(c));

/** Les colonnes choisies et mémorisées (`stats-ventes-objectifs`) — cible, ventes et atteinte ne se masquent jamais. */
export function useObjectiveColumns(enabled: boolean, onSaveFailed: (message: string) => void) {
  return useStatsColumns('stats-ventes-objectifs', OBJECTIVE_COLUMNS, LOCKED, pickerLabel, enabled, onSaveFailed);
}

/** La période par défaut : le mois en cours, du premier au dernier jour — ses jours à venir portent une cible. */
export function defaultObjectiveWindow(today: Date): StatsWindow {
  return { mode: 'dates', from: isoOf(new Date(today.getFullYear(), today.getMonth(), 1)), to: isoOf(new Date(today.getFullYear(), today.getMonth() + 1, 0)) };
}

/** Ce qu'une ligne (ou le total) donne à chaque colonne. */
interface Values { obj: number | null; caHT: number | null; att: number | null; cumO: number | null; cumC: number | null; ecC: number | null; evo: number | null; a: SalesSummaryTotals | null; n1: SalesSummaryTotals | null }

/** La valeur brute d'une colonne. */
function valueOf(v: Values, k: string): number | null {
  switch (k) {
    case 'obj': return v.obj;
    case 'caHT': return v.caHT;
    case 'att': return v.att;
    case 'cumO': return v.cumO;
    case 'cumC': return v.cumC;
    case 'ecC': return v.ecC;
    case 'evo': return v.evo;
    case 'caN1': return v.n1?.netHtCents ?? null;
    case 'ca': return v.a?.netTtcCents ?? null;
    case 'ivN1': return v.n1?.idvX100 ?? null;
    case 'iv': return v.a?.idvX100 ?? null;
    case 'pmN1': return v.n1?.basketCents ?? null;
    case 'pm': return v.a?.basketCents ?? null;
    case 'ttN1': return v.n1?.conversionBp ?? null;
    case 'tt': return v.a?.conversionBp ?? null;
    case 'ppN1': return v.n1?.passages ?? null;
    case 'pp': return v.a?.passages ?? null;
    case 'nvN1': return v.n1?.tickets ?? null;
    case 'nv': return v.a?.tickets ?? null;
    case 'naN1': return v.n1?.qtySold ?? null;
    case 'na': return v.a?.qtySold ?? null;
    case 'mrg': return v.a?.marginHtCents ?? null;
    case 'mrgP': return v.a?.marginRateBp ?? null;
    case 'rem': return v.a?.discountRateBp ?? null;
    default: return null;
  }
}

const rowValues = (r: ObjectiveTrackingRow): Values => ({
  obj: r.objectiveCents, caHT: r.a?.netHtCents ?? null, att: r.attainmentBp, cumO: r.cumObjectiveCents, cumC: r.cumRealizedHtCents,
  ecC: r.cumGapCents, evo: r.growthBp, a: r.a, n1: r.n1,
});
const totalValues = (v: ObjectiveTrackingView): Values => ({
  obj: v.totals.daysWithObjective ? v.totals.objectiveCents : null, caHT: v.totals.a?.netHtCents ?? null, att: v.totals.attainmentBp,
  cumO: v.totals.daysWithObjective ? v.totals.objectiveCents : null, cumC: v.totals.a?.netHtCents ?? null, ecC: v.totals.gapCents,
  evo: v.totals.growthBp, a: v.totals.a, n1: v.totals.n1,
});

/** Une valeur mise en forme — « — » quand elle n'est pas mesurée, signée pour un écart ou une évolution. */
function shown(c: ObjectiveColumn, x: number | null): string {
  if (x === null) return '—';
  if (c.k === 'evo') return dTx(x);
  const sign = c.k === 'ecC' && x > 0 ? '+' : '';
  if (c.unit === 'money') return sign + eur0(x);
  if (c.unit === 'rate') return pc(x);
  if (c.unit === 'idv') return nombre(x / 100, { fractionDigits: 2 });
  return nombre(x);
}

/** La teinte d'une valeur : l'atteinte, l'écart et l'évolution se lisent d'un coup d'œil ; l'an dernier reste en retrait. */
function tone(c: ObjectiveColumn, x: number | null): string {
  if (x === null) return 'var(--ink-300)';
  if (c.n1) return 'var(--ink-500)';
  if (c.k === 'att') return x >= 10_000 ? 'var(--teal-800)' : x >= 9_000 ? 'var(--amber-fg)' : 'var(--rose-fg)';
  if (c.k === 'ecC' || c.k === 'evo') return x >= 0 ? 'var(--teal-800)' : 'var(--rose-fg)';
  if (c.k === 'mrgP' && x < 2_500) return 'var(--rose-fg)';
  if (c.k === 'rem' && x > 1_200) return 'var(--amber-fg)';
  return 'var(--ink-800)';
}

/** `AAAAMMJJ` → une date locale. */
const dateOfYmd = (ymd: string): Date => new Date(Number(ymd.slice(0, 4)), Number(ymd.slice(4, 6)) - 1, Number(ymd.slice(6, 8)));

/** Le nom court d'un jour (« sam. 03/10 »), dans la langue du compte. */
function dayLabel(ymd: string, lang: string): string {
  return `${new Intl.DateTimeFormat(lang, { weekday: 'short' }).format(dateOfYmd(ymd))} ${ymd.slice(6, 8)}/${ymd.slice(4, 6)}`;
}

/**
 * Ce qu'une ligne dit d'elle-même sous sa date : fermé, fermeture, à venir, événement, fixé.
 * ⚠️ UN ÉVÉNEMENT À VENIR SE DIT AUSSI : c'est avant le jour qu'il sert à préparer la journée ; « à venir » seul le tairait.
 * ⚠️ UN JOUR FIXÉ LE DIT : sa cible a été décidée à la main, elle ne suit plus le poids de son jour de semaine.
 */
function dayTag(r: ObjectiveTrackingRow): string {
  if (r.closed) return r.exception?.kind === 'closed' ? t('fermeture') : t('fermé');
  const own = r.exception?.kind === 'event' ? t('événement {variation}', { variation: dTx(r.exception.changeBp) })
    : r.exception?.kind === 'fixed' ? t('fixé') : '';
  return [...(r.future ? [t('à venir')] : []), ...(own ? [own] : [])].join(' · ');
}

// ─── Les indicateurs ─────────────────────────────────────────────────────────────────────────────

/** Une tuile d'indicateur du suivi : son nom, sa valeur, ce qui l'explique, et sa petite courbe quand elle en a une. */
function ObjectiveKpi({ label, icon, tone: k, value, sub, series }: { label: string; icon: IconName; tone: Tone; value: string; sub: string; series?: (number | null)[] }) {
  const sp = sparkPaths(series ?? []);
  return (
    <div style={KPI_TILE}>
      <KpiLabel label={label} icon={icon} tone={k} />
      <div style={{ fontSize: 'var(--t-feature)', fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums', marginTop: 8, whiteSpace: 'nowrap' }}>{value}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 5, minWidth: 0 }}>
        <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>{sub}</span>
      </div>
      <svg viewBox="0 0 100 30" preserveAspectRatio="none" aria-hidden style={{ display: 'block', width: `calc(100% + ${2 * KPI_PAD_X}px)`, height: 34, margin: `8px -${KPI_PAD_X}px 0` }}>
        <path d={sp.area} style={{ fill: `color-mix(in oklch, ${TONES[k][2]} 16%, transparent)`, stroke: 'none' }} />
        <path d={sp.line} vectorEffect="non-scaling-stroke" style={{ fill: 'none', stroke: TONES[k][2], strokeWidth: 1.8, strokeLinejoin: 'round' }} />
      </svg>
    </div>
  );
}

// ─── L'onglet ────────────────────────────────────────────────────────────────────────────────────

const CARD: CSSProperties = { background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', overflow: 'hidden', marginBottom: 13 };

/** La phrase du pied : d'où viennent les poids qui ont réparti chaque mois. */
const FOOTNOTES: Record<ObjectiveTrackingView['weightsSource'], Translatable> = {
  profile: key('Objectif du jour = objectif du mois × poids du jour de semaine (profil réel des 52 dernières semaines), jours exceptionnels compris. Atteinte en HT ; N-1 = le même jour de semaine, 52 semaines plus tôt. Taux recalculés sur les sommes.'),
  manual: key('Objectif du jour = objectif du mois × poids du jour de semaine (saisis dans Ventes & fiscal › Objectifs), jours exceptionnels compris. Atteinte en HT ; N-1 = le même jour de semaine, 52 semaines plus tôt. Taux recalculés sur les sommes.'),
  flat: key('Objectif du jour = objectif du mois réparti également sur les jours d’ouverture (aucune vente à lire pour en tirer le profil des jours), jours exceptionnels compris. Atteinte en HT ; N-1 = le même jour de semaine, 52 semaines plus tôt. Taux recalculés sur les sommes.'),
};

/** Le suivi d'une période déjà lue : indicateurs, course à l'objectif, jour après jour. */
export function StatsObjectives({ view, visible, columnsButton, busy, lang }: {
  view: ObjectiveTrackingView; visible: readonly ObjectiveColumn[]; columnsButton: ReactNode; busy: boolean; lang: string;
}) {
  const rows = view.rows;
  const { tipProps, tip } = useChartTip();
  const n = rows.length;
  const tt = view.totals;
  const last = rows.reduce((l, r, i) => (r.a ? i : l), -1);
  // Les courbes cumulées, en HT : l'objectif sur toute la période, les ventes et l'an dernier jusqu'au dernier jour écoulé.
  const curO: number[] = [];
  const curC: number[] = [];
  const curN: number[] = [];
  let o = 0;
  let c = 0;
  let nn = 0;
  rows.forEach((r, i) => {
    o += r.objectiveCents ?? 0;
    curO.push(o);
    if (i <= last) { c += r.a?.netHtCents ?? 0; nn += r.n1?.netHtCents ?? 0; curC.push(c); curN.push(nn); }
  });
  const projection = tt.projectionCents;
  const top = niceTop(Math.max(0, ...curO, ...curC, ...curN, projection ?? 0));
  const X = (i: number) => ((i + 0.5) / (n || 1)) * 1000;
  const Y = (v: number) => 1000 - (v / top) * 1000;
  const path = (arr: number[]) => (arr.length ? monotonePath(arr.map((v, i) => [X(i), Y(v)])) : '');
  const lineC = path(curC);
  const areaC = curC.length ? `${lineC}L${X(curC.length - 1).toFixed(1)},1000L${X(0).toFixed(1)},1000Z` : '';
  const projPath = projection !== null && last >= 0 ? `M${X(last).toFixed(1)},${Y(c).toFixed(1)}L${X(n - 1).toFixed(1)},${Y(projection).toFixed(1)}` : '';
  const todayIndex = rows.findIndex((r) => r.day === view.today);
  const step = Math.max(1, Math.ceil(n / 8));
  const hasObjective = tt.daysWithObjective > 0;

  const insight = last < 0 ? t('Aucun jour écoulé sur la période.')
    : [
      t('Atteinte {taux} à date', { taux: pc(tt.attainmentBp) }),
      ...(tt.projectionBp !== null ? [t('au rythme actuel : {taux} en fin de période', { taux: pc(tt.projectionBp) })] : []),
      ...(tt.growthBp !== null ? [t('{evolution} vs N-1', { evolution: dTx(tt.growthBp) })] : []),
    ].join(' · ');
  const kpis: { label: Translatable; icon: IconName; tone: Tone; value: string; sub: string; series?: (number | null)[] }[] = [
    { label: key('Objectif HT'), icon: 'target', tone: 'amber', value: hasObjective ? eur0(tt.objectiveCents) : '—', sub: t('{n} jour avec objectif', { n: tt.daysWithObjective }) },
    { label: key('Réalisé HT'), icon: 'trendUp', tone: 'teal', value: eur0(tt.a?.netHtCents ?? null), sub: t('à date · TTC {montant}', { montant: eur0(tt.a?.netTtcCents ?? null) }), series: curC },
    {
      label: key('Atteinte à date'), icon: 'check', tone: 'teal', value: pc(tt.attainmentBp), sub: t('face à l’objectif des jours passés'),
      series: curC.map((v, i) => (curO[i] ? (v / curO[i]!) * 100 : null)),
    },
    {
      label: key('Écart cumulé'), icon: 'compare', tone: tt.gapCents !== null && tt.gapCents < 0 ? 'rose' : 'teal',
      value: tt.gapCents === null ? '—' : `${tt.gapCents > 0 ? '+' : ''}${eur0(tt.gapCents)}`,
      sub: tt.gapCents === null ? t('aucun jour écoulé') : tt.gapCents >= 0 ? t('d’avance sur la cible') : t('de retard sur la cible'),
    },
    { label: key('Évolution N-1'), icon: 'refresh', tone: 'blue', value: dTx(tt.growthBp), sub: t('HT, même jour de semaine') },
    tt.daysLeft > 0
      ? {
        label: key('Reste à faire / jour'), icon: 'calendar', tone: 'violet',
        value: tt.needPerDayCents === 0 ? t('atteint') : eur0(tt.needPerDayCents),
        sub: t('{jours} · projection {taux}', { jours: t('{n} jour restant', { n: tt.daysLeft }), taux: pc(tt.projectionBp) }),
      }
      : { label: key('Projection'), icon: 'calendar', tone: 'violet', value: '—', sub: last < 0 ? t('période à venir') : t('période close') },
  ];
  const grid = `150px ${visible.map((x) => (x.k === 'att' ? '96px' : '112px')).join(' ')}`;
  const totals = totalValues(view);

  return (
    <div aria-busy={busy} style={{ opacity: busy ? 0.55 : 1, transition: 'opacity .15s' }}>
      {(view.missing.length > 0 || view.undistributed.length > 0 || view.overcommitted.length > 0) && (
        <div role="note" style={{ ...CARD, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '11px 14px', background: 'var(--amber-bg)', borderColor: 'transparent' }}>
          <Icon name="info" size={15} stroke={2} color="var(--amber-fg)" />
          <span style={{ flex: 1, minWidth: 220, fontSize: 'var(--t-body)', color: 'var(--amber-fg)', lineHeight: 1.5 }}>
            {[
              ...(view.missing.length ? [t('Sans objectif : {mois}.', { mois: view.missing.map((m) => moisLongLisible(m, lang)).join(', ') })] : []),
              ...(view.undistributed.length ? [t('Sans aucun jour ouvert où se poser : {mois}.', { mois: view.undistributed.map((m) => moisLongLisible(m, lang)).join(', ') })] : []),
              ...(view.overcommitted.length ? [t('Jours fixés au-delà de l’objectif du mois : {mois}.', { mois: view.overcommitted.map((m) => moisLongLisible(m, lang)).join(', ') })] : []),
            ].join(' ')}
          </span>
          <Link to="/objectifs" style={{ fontSize: 'var(--t-body)', fontWeight: 700, color: 'var(--amber-fg)' }}>{t('Saisir les objectifs')}</Link>
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(176px,1fr))', gap: 11, marginBottom: 13 }}>
        {kpis.map((k) => <ObjectiveKpi key={keyText(k.label)} label={t(k.label)} icon={k.icon} tone={k.tone} value={k.value} sub={k.sub} series={k.series} />)}
      </div>

      <div style={CARD}>
        <CardHead icon="target" tone="amber" title={t('La course à l’objectif')} insight={insight} />
        <div style={{ padding: '8px 16px 0' }}>
          <div style={{ position: 'relative', height: 220, marginLeft: 48 }}>
            {[0, 0.25, 0.5, 0.75, 1].map((f) => (
              <div key={f}>
                <div style={{ position: 'absolute', left: 0, right: 0, bottom: `${f * 100}%`, borderTop: '1px dashed var(--ink-200)' }} />
                <span style={{
                  position: 'absolute', right: 'calc(100% + 8px)', transform: 'translateY(50%)', fontSize: 'var(--t-sub)', color: 'var(--ink-500)',
                  fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', bottom: `${f * 100}%`,
                }}>{axisLabel(top * f, 'eur')}</span>
              </div>
            ))}
            <svg viewBox="0 0 1000 1000" preserveAspectRatio="none" aria-hidden style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', overflow: 'visible', pointerEvents: 'none' }}>
              <defs>
                <linearGradient id="statsObjGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0" style={{ stopColor: 'var(--teal-500)', stopOpacity: 0.28 }} />
                  <stop offset="1" style={{ stopColor: 'var(--teal-500)', stopOpacity: 0 }} />
                </linearGradient>
              </defs>
              <path d={areaC} style={{ fill: 'url(#statsObjGrad)', stroke: 'none' }} />
              <path d={path(curN)} vectorEffect="non-scaling-stroke" style={{ fill: 'none', stroke: 'var(--blue)', strokeWidth: 2, strokeDasharray: '6 5' }} />
              <path d={path(curO)} vectorEffect="non-scaling-stroke" style={{ fill: 'none', stroke: 'var(--amber)', strokeWidth: 2.4, strokeDasharray: '2 5', strokeLinecap: 'round' }} />
              <path d={projPath} vectorEffect="non-scaling-stroke" style={{ fill: 'none', stroke: 'var(--teal-600)', strokeWidth: 2, strokeDasharray: '7 6', opacity: 0.7 }} />
              <path d={lineC} vectorEffect="non-scaling-stroke" style={{ fill: 'none', stroke: 'var(--teal-600)', strokeWidth: 2.8, strokeLinejoin: 'round', strokeLinecap: 'round' }} />
            </svg>
            {todayIndex >= 0 && (
              <div style={{ position: 'absolute', top: -6, bottom: 0, left: `${((todayIndex + 0.5) / n) * 100}%`, borderLeft: '1.5px solid var(--ink-400)', pointerEvents: 'none' }}>
                <span style={{ position: 'absolute', top: -12, left: 4, fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--ink-500)', whiteSpace: 'nowrap' }}>{t('aujourd’hui')}</span>
              </div>
            )}
            <div role="list" style={{ position: 'absolute', inset: 0, display: 'flex', zIndex: 3 }}>
              {rows.map((r, i) => (
                <div key={r.day} role="listitem" className="oz-stats-ev-col"
                  {...tipProps({
                    label: r.closed ? `${dayLabel(r.day, lang)} · ${t('fermé')}` : dayLabel(r.day, lang),
                    value: i <= last ? t('ventes cumulées {montant}', { montant: eur0(curC[i]!) }) : t('à venir'),
                    detail: [t('objectif cumulé {montant}', { montant: eur0(curO[i]!) }), ...(i <= last ? [t('N-1 {montant}', { montant: eur0(curN[i]!) })] : [])].join(' · '),
                  })}
                  style={{ flex: 1, minWidth: 0, height: '100%', borderRadius: 4 }} />
              ))}
            </div>
            {tip}
          </div>
          <div style={{ position: 'relative', height: 24, marginLeft: 48 }}>
            {rows.map((r, i) => (i % step === 0 ? (
              <span key={r.day} style={{ position: 'absolute', top: 7, left: `${((i + 0.5) / n) * 100}%`, transform: 'translateX(-50%)', fontSize: 'var(--t-sub)', color: 'var(--ink-500)', whiteSpace: 'nowrap' }}>
                {dayLabel(r.day, lang)}
              </span>
            ) : null))}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px 18px', flexWrap: 'wrap', padding: '9px 16px 13px', borderTop: '1px solid var(--ink-100)', fontSize: 'var(--t-note)', color: 'var(--ink-600)' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}><span style={{ width: 18, height: 3, borderRadius: 2, background: 'var(--teal-600)' }} />{t('Ventes HT cumulées')}</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}><span style={{ width: 18, borderTop: '2.5px dotted var(--amber)' }} />{t('Objectif cumulé')}</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}><span style={{ width: 18, borderTop: '2px dashed var(--blue)' }} />{t('N-1 cumulé')}</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}><span style={{ width: 18, borderTop: '2px dashed var(--teal-600)', opacity: 0.7 }} />{t('Projection au rythme actuel')}</span>
        </div>
      </div>

      <div style={CARD}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '10px 13px', borderBottom: '1px solid var(--ink-200)' }}>
          <span style={{ flex: 1, minWidth: 200, fontSize: 'var(--t-control)', fontWeight: 700, color: 'var(--ink-900)' }}>{t('Jour après jour')}</span>
          {columnsButton}
        </div>
        <div style={{ overflowX: 'auto', maxHeight: '70vh', overflowY: 'auto' }}>
          <div role="table" aria-label={t('Jour après jour')} style={{ minWidth: 'max-content' }}>
            <div style={{ position: 'sticky', top: 0, zIndex: 3 }}>
              <div role="row" style={{ display: 'grid', gridTemplateColumns: grid, background: 'var(--ink-50)', borderBottom: '1px solid var(--ink-200)' }}>
                <span role="columnheader" style={{ position: 'sticky', left: 0, zIndex: 2, background: 'var(--ink-50)', padding: '12px 12px 9px', fontSize: 'var(--t-sub)', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--ink-700)', borderRight: '1px solid var(--ink-200)' }}>{t('Jour')}</span>
                {visible.map((x) => (
                  <span key={x.k} role="columnheader" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'flex-end', padding: '8px 10px 9px', background: x.n1 ? 'var(--ink-100)' : 'var(--ink-50)' }}>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 8.5, letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--ink-500)', lineHeight: 1, marginBottom: 2 }}>{x.n1 ? t('N-1') : ''}</span>
                    <span style={{ fontSize: 'var(--t-sub)', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', whiteSpace: 'nowrap', color: x.n1 ? 'var(--ink-500)' : 'var(--ink-700)' }}>{columnLabel(x)}</span>
                  </span>
                ))}
              </div>
              <div role="row" style={{ display: 'grid', gridTemplateColumns: grid, background: 'var(--teal-50)', borderBottom: '1.5px solid var(--teal-400)' }}>
                <span role="rowheader" style={{ position: 'sticky', left: 0, zIndex: 2, background: 'var(--teal-50)', padding: 10, fontSize: 'var(--t-body)', fontWeight: 800, color: 'var(--ink-900)', borderRight: '1px solid var(--ink-200)' }}>{t('Total')}</span>
                {visible.map((x) => {
                  const v = valueOf(totals, x.k);
                  return <span key={x.k} role="cell" style={{ display: 'flex', justifyContent: 'flex-end', padding: 10, fontSize: 'var(--t-body)', fontWeight: 800, color: tone(x, v), fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{shown(x, v)}</span>;
                })}
              </div>
            </div>
            {rows.map((r, i) => {
              const bg = i % 2 ? 'var(--ink-50)' : 'var(--paper)';
              const vals = rowValues(r);
              const tag = dayTag(r);
              return (
                <div key={r.day} role="row" style={{ display: 'grid', gridTemplateColumns: grid, background: bg, borderBottom: '1px solid var(--ink-100)' }}>
                  <span role="rowheader" style={{ position: 'sticky', left: 0, zIndex: 1, background: bg, padding: '8px 12px', borderRight: '1px solid var(--ink-200)', minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 'var(--t-body)', fontWeight: 700, color: r.closed || r.future ? 'var(--ink-400)' : 'var(--ink-900)', whiteSpace: 'nowrap' }}>{dayLabel(r.day, lang)}</span>
                    <span style={{ display: 'block', fontSize: 'var(--t-sub)', color: 'var(--ink-500)', whiteSpace: 'nowrap' }}>{tag}</span>
                  </span>
                  {visible.map((x) => {
                    const v = valueOf(vals, x.k);
                    return (
                      <span key={x.k} role="cell" style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '8px 10px', background: x.n1 ? 'var(--ink-50)' : 'transparent',
                        fontSize: 'var(--t-body)', fontWeight: x.k === 'caHT' || x.k === 'att' ? 700 : x.n1 ? 500 : 600, color: tone(x, v), fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
                      }}>{shown(x, v)}</span>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
        <div style={{ padding: '10px 14px', fontSize: 'var(--t-meta)', color: 'var(--ink-500)', background: 'var(--ink-50)', lineHeight: 1.5 }}>{t(FOOTNOTES[view.weightsSource])}</div>
      </div>
    </div>
  );
}

/** « On exporte ce que l'on voit » : les colonnes affichées, la ligne de total, puis un jour par ligne. */
export function exportObjectives(view: ObjectiveTrackingView, visible: readonly ObjectiveColumn[]): void {
  const cell = (c: ObjectiveColumn, x: number | null): string => {
    if (x === null) return '';
    if (c.unit === 'money') return toInputValue(x);
    if (c.unit === 'rate') return csvRate(x);
    if (c.unit === 'idv') return toInputValue(x, 2);
    return csvQty(x);
  };
  const head = (c: ObjectiveColumn): string => {
    const l = c.n1 ? t('{colonne} N-1', { colonne: columnLabel(c) }) : columnLabel(c);
    return c.unit === 'rate' ? `${l} (%)` : c.unit === 'money' ? `${l} (${currentCurrency().code})` : l;
  };
  const totals = totalValues(view);
  exporterCsv(`objectifs-${view.period.from}-${view.period.to}`, [t('Jour'), ...visible.map(head)], [
    [t('TOTAL'), ...visible.map((c) => cell(c, valueOf(totals, c.k)))],
    ...view.rows.map((r) => [`${r.day.slice(0, 4)}-${r.day.slice(4, 6)}-${r.day.slice(6, 8)}`, ...visible.map((c) => cell(c, valueOf(rowValues(r), c.k)))]),
  ]);
}
